<template>
  <modal-container>
    <modal-default
      ref="modal"
      :title="`${this.typeLabelHeading} - Validation Errors`"
      is-wide
    >
      <div class="modal-inner">
        <div class="fields-container mb-200">
          <grid-row :gap="2">
            <grid-column :size="6">
              <form-field-input
                label="Label"
                disabled=""
                :value="port.label || 'Unlabeled'"
              ></form-field-input>
            </grid-column>
            <grid-column :size="6">
              <form-field-input
                label="File"
                disabled=""
                :value="port.file_name"
              ></form-field-input>
            </grid-column>
          </grid-row>

          <grid-row :gap="2">
            <grid-column :size="6">
              <form-field-input
                label="Current Status"
                disabled=""
                :value="$utils.newGetEnumLabel(this.port.status, ENUMS.ImportStatusEnum)"
              ></form-field-input>
            </grid-column>
            <grid-column :size="6">
              <form-field-input
                label="Created At"
                disabled=""
                :value="createdDate"
              ></form-field-input>
            </grid-column>
          </grid-row>
        </div>

        <div class="warnings-container mb-200" v-if="warningCount > 0">
          <div class="container-heading">
            <icon-default icon="warning" size="is-small" color="is-warning" class="text-vertical-bottom"></icon-default>
            <strong>Total Warnings: {{ warningCount }}</strong>
          </div>

          <div class="message is-warning my-100">
            <div class="message-body py-100">
              <p>Import warnings you should take a look at. You may import with warnings.</p>
            </div>
          </div>

          <import-validation-modal-warning-box
            :warnings="port.status_errors"
            title="Missing Statuses"
            hint="The following statuses do not exist in Forefront. If you want to import records into the following statuses you will need to create the statuses on the Pipeline page first. Otherwise, any record that doesn’t have a corresponding statuses will be given the status of ‘New’."
          ></import-validation-modal-warning-box>

          <import-validation-modal-warning-box
            :warnings="port.campaign_errors"
            title="Missing Campaigns"
            hint="The following campaigns do not exist in Forefront. If you want imported records to be associated with the following campaigns you will need to create the campaigns on the Campaign page first. Otherwise, any record that doesn’t have a corresponding campaign will not be associated with a campaign."
          ></import-validation-modal-warning-box>

          <import-validation-modal-warning-box
            :warnings="port.column_header_errors.extra_headers"
            title="Extra Headers"
            hint="Your import file has the following columns that don’t have a corresponding field in Forefront. Any information in these columns will not be imported into Forefront."
          ></import-validation-modal-warning-box>

          <import-validation-modal-warning-box
            :warnings="port.column_header_errors.missing_headers"
            title="Missing Headers"
            :hint="`Your import file doesn’t have the following columns that have the corresponding fields in Forefront. Any ${this.typeLabel} you import from this file will have empty values for these fields.`"
          ></import-validation-modal-warning-box>

          <import-validation-modal-warning-box
            :warnings="port.duplicates"
            title="Duplicates"
            :hint="`Your import file has some ${this.typeLabel} that might match existing ${this.typeLabel} you already have in Forefront. You should double-check to see if they match and remove them from your file if they do.`"
          ></import-validation-modal-warning-box>
        </div>

        <div class="errors-container mb-200" v-if="errorCount > 0">
          <div class="container-heading">
            <icon-default icon="error" size="is-small" color="is-danger" class="text-vertical-bottom"></icon-default>
            <strong>{{ this.typeLabelHeading }} With Errors: {{ recordCountWithErrors }}</strong>
          </div>

          <div class="message is-danger my-100">
            <div class="message-body py-100">
              <p>Unable to import with errors. Please fix the following errors and try importing again.</p>
            </div>
          </div>

          <div v-for="(prop, record) in validationErrors" :key="record">
            <div class="error-box has-border mb-50 p-50" v-if="record !== 'id'">
              <div class="error-title">
                <strong>
                  {{ record }} | {{ prop.address }} -
                  <span class="error-box-count small-text">{{ prop.errors.length }} {{ prop.errors.length > 1 ? 'errors' : 'error' }}</span>
                </strong>
              </div>

              <div v-for="(error, index) in prop.errors" :key="index" class="has-border-bottom p-50 pb-25 m-25">
                <p class="small-text">- <strong>{{ error.name }}</strong>: {{ error.message }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal-default>
  </modal-container>
</template>

<script>
import ImportValidationModalWarningBox from './ImportValidationModalWarningBox';
import dates from "../../../services/dates";
import {ENUMS} from "../../../services/enums";

export default {
  name: 'ImportValidationModal',
  components: { ImportValidationModalWarningBox },
  props: {
    port: {
      type: Object,
      required: true
    },
    errorCount: {
      type: Number,
      required: true
    },
    warningCount: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      validationErrors: {}
    }
  },
  computed: {
    typeLabelHeading() {
      return (this.port.type === ENUMS.ImportTypeEnum.Record.value) ? 'Leads' : 'Contacts'
    },
    typeLabel() {
      return (this.port.type === ENUMS.ImportTypeEnum.Record.value) ? 'leads' : 'contacts'
    },
    createdDate() {
      return dates.formatAsDateTime(this.port.created_at);
    },
    recordCountWithErrors() {
      return Object.keys(this.validationErrors).length - 1;
    }
  },
  methods: {
    open() {
      this.setValidationErrors();
      this.$refs.modal.open();
    },
    setValidationErrors() {
      const storeErr = this.$store.getters.importsValidationErrors.find(i => i.id === this.port.id)
      if (storeErr) {
        this.validationErrors = this.$utils.clone(storeErr)
        return;
      }

      this.validationErrors = { id: this.port.id }
      const errors = this.$utils.clone(this.port.validation_errors)

      for (let [key, messages] of Object.entries(errors)) {
        const recordNumber = `Row ${key.substring(0, key.indexOf('.'))}`;
        const name = key.substring(key.indexOf('.') + 1);
        const obj = { name, message: messages.join(' - ') };

        if (name === 'property_address_error_header') {
          if (this.validationErrors[recordNumber]) {
            this.validationErrors[recordNumber].address = obj.message;
          } else {
            this.validationErrors[recordNumber] = { errors: [], address: obj.message };
          }
          continue;
        }

        if (this.validationErrors[recordNumber]) {
          this.validationErrors[recordNumber].errors.push(obj);
        } else {
          this.validationErrors[recordNumber] = { errors: [obj] };
        }
      }

      this.$store.commit('pushImportsValidationErrors', this.validationErrors);
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.error-box-count { color: $text-muted; }
</style>
